import countries from 'world-countries';
import { getCountryCallingCode } from 'libphonenumber-js/max';
import type { CountryCode as LibCountryCode } from 'libphonenumber-js/core';

export interface CountryCode {
  code: string;
  dialCode: string;
  name: string;
  flag: string;
}

const codeToFlag = (cca2: string) => cca2
  .toUpperCase()
  .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

const buildCountryList = (): CountryCode[] => {
  const list: CountryCode[] = [];
  const latinAmerica: Set<string> = new Set([
    'AR', 'BO', 'BR', 'CL', 'CO', 'CR', 'CU', 'DO', 'EC', 'SV',
    'GT', 'HN', 'MX', 'NI', 'PA', 'PY', 'PE', 'PR', 'UY', 'VE',
  ]);
  for (const c of countries) {
    try {
      const calling = getCountryCallingCode(c.cca2 as LibCountryCode);
      list.push({
        code: c.cca2,
        dialCode: `+${calling}`,
        name: c.name.common,
        flag: codeToFlag(c.cca2),
      });
    } catch {
      // skip unsupported entries
    }
  }
  return list.sort((a, b) => {
    const aIsLatam = latinAmerica.has(a.code);
    const bIsLatam = latinAmerica.has(b.code);
    if (aIsLatam && !bIsLatam) {
      return -1;
    }
    if (!aIsLatam && bIsLatam) {
      return 1;
    }
    // Within LATAM, put Argentina first
    if (aIsLatam && bIsLatam) {
      if (a.code === 'AR' && b.code !== 'AR') {
        return -1;
      }
      if (b.code === 'AR' && a.code !== 'AR') {
        return 1;
      }
    }
    return a.name.localeCompare(b.name);
  });
};

export const countryCodes: CountryCode[] = buildCountryList();

export const getCountryByCode = (code: string): CountryCode | undefined => countryCodes.find((country) => country.code === code);

export const getCountryByDialCode = (dialCode: string): CountryCode | undefined => countryCodes.find((country) => country.dialCode === dialCode);

export const defaultCountryCode = 'AR'; // Argentina as default
